import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { STATUS } from 'projectConstants';
import apiService from 'services';
import bloxBiService from 'services/bloxBiService';
import {
  type ReportingWindow,
  type ReportingContext,
  resolveReportingPeriod,
  getCurrentMonthYyyyMm,
  formatMonth,
} from 'utils/reportingPeriodUtils';
import { buildEmptyDashboardBody, suggestNewDashboardName } from 'utils/dashboardBootstrap';
import { readSelectedDashboardId, saveSelectedDashboardId } from 'utils/dashboardSelectionStorage';

export type ModelDashboardListItem = {
  id: string;
  name?: string | null;
  description?: string | null;
  model_id?: number | null;
  page_count: number;
  created_at?: string;
  updated_at?: string;
};

const mapDashboardListRow = (row: any): ModelDashboardListItem => ({
  id: String(row.id),
  name: row.name,
  description: row.description,
  model_id: row.model_id,
  page_count: row.page_count ?? row.pages?.length ?? 0,
  created_at: row.created_at,
  updated_at: row.updated_at,
});

const pickDashboardToLoad = (
  list: ModelDashboardListItem[],
  modelId: number | string,
): ModelDashboardListItem | null => {
  if (list.length === 0) return null;
  const persisted = readSelectedDashboardId(modelId);
  if (persisted) {
    const match = list.find((item) => item.id === persisted);
    if (match) return match;
  }
  return list[0];
};

export type ResolveIndicatorDimensionArguments = {
  blockDetails?: any[] | null;
  blockId?: number | string | null;
  indicatorId?: number | string | null;
  dimensionId?: number | string | null;
};

/**
 * Resolve dashboard breakdown dimension metadata from merged `dashboardBlocks`
 * (native + connected dimensions already flattened into `indicator_dimensions`).
 *
 * Compare IDs with Number(...) so string/number mismatches from BI vs modelAPI do not break lookups.
 */
export function resolveIndicatorDimension({
  blockDetails,
  blockId,
  indicatorId,
  dimensionId,
}: ResolveIndicatorDimensionArguments): any | undefined {
  if (
    blockId == null ||
    indicatorId == null ||
    dimensionId == null ||
    !Array.isArray(blockDetails) ||
    blockDetails.length === 0
  ) {
    return undefined;
  }

  const bid = Number(blockId);
  const iid = Number(indicatorId);
  const did = Number(dimensionId);

  if (!Number.isFinite(bid) || !Number.isFinite(iid) || !Number.isFinite(did)) {
    return undefined;
  }

  const foundBlock = blockDetails.find((block: any) => Number(block.block_id) === bid);
  if (!foundBlock?.indicators?.length) return undefined;

  const foundIndicator = foundBlock.indicators.find((ind: any) => {
    const rawId = ind.indicator_id ?? ind.id;
    return Number(rawId) === iid;
  });
  if (!foundIndicator?.indicator_dimensions?.length) return undefined;

  return foundIndicator.indicator_dimensions.find(
    (dim: any) => Number(dim.indicator_dimension_id) === did,
  );
}

function enrichInstrumentIndicatorDimensionName(ii: any, blockDetails: any[]): any {
  if (ii?.dimension_id == null || ii?.block_id == null) return ii;
  const found = resolveIndicatorDimension({
    blockDetails,
    blockId: ii.block_id,
    indicatorId: ii.indicator_id ?? ii.id,
    dimensionId: ii.dimension_id,
  });
  const name = found?.indicator_dimension_name ?? ii.dimension_name;
  return name ? { ...ii, dimension_name: name } : ii;
}

function enrichAllInstrumentsInstrumentIndicatorsDimensionNames(
  blockDetails: any[],
  instruments: any[] | undefined,
): any[] {
  if (!Array.isArray(instruments)) return instruments ?? [];
  if (!Array.isArray(blockDetails) || blockDetails.length === 0) return instruments;

  return instruments.map((instrument: any) => {
    if (!Array.isArray(instrument?.instrument_indicators)) return instrument;
    return {
      ...instrument,
      instrument_indicators: instrument.instrument_indicators.map((ii: any) =>
        enrichInstrumentIndicatorDimensionName(ii, blockDetails),
      ),
    };
  });
}

function enrichInstrumentOutputsDimensionNames(blockDetails: any[], outputs: any): any {
  if (!outputs || typeof outputs !== 'object') return outputs;
  if (!Array.isArray(blockDetails) || blockDetails.length === 0) return outputs;

  const result: Record<string, unknown> = {};
  Object.keys(outputs).forEach((key) => {
    const entry = outputs[key] as any;
    if (!entry?.data || !Array.isArray(entry.data)) {
      result[key] = entry;
      return;
    }
    result[key] = {
      ...entry,
      data: entry.data.map((row: any) => {
        const ind = row?.indicator;
        if (!ind?.dimension_id || !ind?.block_id) return row;
        const found = resolveIndicatorDimension({
          blockDetails,
          blockId: ind.block_id,
          indicatorId: ind.indicator_id ?? ind.id,
          dimensionId: ind.dimension_id,
        });
        const name = found?.indicator_dimension_name ?? ind.dimension_name;
        return name ? { ...row, indicator: { ...ind, dimension_name: name } } : row;
      }),
    };
  });
  return result;
}

interface DashboardProperties {
  dashboard: any;
  isPageBarOpen: boolean;
  selectedPage: any;
  isPageModalOpen: boolean;
  editPageData: any;
  createPageStatus: keyof typeof STATUS;
  updatePageStatus: keyof typeof STATUS;
  deletePageStatus: keyof typeof STATUS;
  fetchDashboardStatus: keyof typeof STATUS;
  dashboardBlocks: Array<any>;
  dashboardBlockStatus: keyof typeof STATUS;
  selectedDashboardScenario: any;
  compareDashboardScenario: any;
  dashboardInstruments: Array<any>;
  dashboardInstrumentData: any;
  dashboardInstrumentStatus: keyof typeof STATUS;
  editInstrumentStatus: keyof typeof STATUS;
  editInstrumentData: any;
  editInstrumentOutput: any;
  allInstruments: Array<any>;
  timeFilter: { start: string; end: string } | null;
  allInstrumentOutputs: any;
  instrumentExpandKey: any;
  allInstrumentOutputsStatus: keyof typeof STATUS;
  calculationLoadingStatus: keyof typeof STATUS;
  selectedRow: any;
  /** Legacy display value (still updated for backward compat with PageInfo / TimeRangeFilter). */
  dashboardReportingPeriod: {
    preset: string;
    start: string;
    end: string;
  };
  /** Anchor month for the reporting period in YYYY-MM format (e.g. "2026-03"). */
  reporting_anchor_period: string;
  /** Reporting window relative to the anchor (SINGLE | YTD | FY | ROLLING_12). */
  reporting_window: ReportingWindow;
  /** All dashboard packs for the current model (metadata only). */
  modelDashboards: ModelDashboardListItem[];
  selectedDashboardId: string | null;
  listModelDashboardsStatus: keyof typeof STATUS;
  dashboardPackMutationStatus: keyof typeof STATUS;
}

const initialState: DashboardProperties = {
  dashboard: null,
  isPageBarOpen: false,
  selectedPage: null,
  isPageModalOpen: false,
  editPageData: {},
  createPageStatus: STATUS.IDLE,
  updatePageStatus: STATUS.IDLE,
  deletePageStatus: STATUS.IDLE,
  fetchDashboardStatus: STATUS.IDLE,
  dashboardBlocks: [],
  dashboardBlockStatus: STATUS.IDLE,
  selectedDashboardScenario: null,
  compareDashboardScenario: null,
  dashboardInstruments: [],
  dashboardInstrumentData: {},
  dashboardInstrumentStatus: STATUS.IDLE,
  editInstrumentData: {},
  editInstrumentOutput: {},
  editInstrumentStatus: STATUS.IDLE,
  allInstruments: [], // with edit data
  timeFilter: null,
  allInstrumentOutputs: [], // key(insturmentID): value(insturment Data) output data
  instrumentExpandKey: {},
  allInstrumentOutputsStatus: STATUS.IDLE,
  calculationLoadingStatus: STATUS.IDLE,
  selectedRow: null,
  dashboardReportingPeriod: {
    preset: 'Current Month',
    start: '',
    end: '',
  },
  reporting_anchor_period: getCurrentMonthYyyyMm(),
  reporting_window: 'SINGLE' as ReportingWindow,
  modelDashboards: [],
  selectedDashboardId: null,
  listModelDashboardsStatus: STATUS.IDLE,
  dashboardPackMutationStatus: STATUS.IDLE,
};

const getDimensionId = (dimension: any) =>
  dimension?.id ?? dimension?.dimension_id ?? dimension?.indicator_dimension_id;

const getDimensionName = (dimension: any) =>
  dimension?.name ?? dimension?.dimension_name ?? dimension?.indicator_dimension_name;

const mergeDimensionsById = (dimensions: Array<any> = []) => {
  const dimensionMap = new Map<number, any>();
  dimensions.forEach((dimension: any) => {
    const id = getDimensionId(dimension);
    if (id == null) return;
    dimensionMap.set(Number(id), dimension);
  });
  return Array.from(dimensionMap.values());
};

const normalizeDimensionItems = (payload: any) => {
  const items = Array.isArray(payload) ? payload : payload?.items;
  if (!Array.isArray(items)) return [];

  return items.map((item: any) => ({
    dimension_item_id: item?.dimension_item_id ?? item?.id,
    dimension_item_name: item?.dimension_item_name ?? item?.name,
  }));
};

const mapDimensionItemsToIndicatorItems = (items: Array<any> = []) =>
  items.map((item: any) => ({
    indicator_dimension_item_id: item?.dimension_item_id ?? item?.id,
    indicator_dimension_item_name: item?.dimension_item_name ?? item?.name,
  }));

const updateDashboardblockData = (blocksData: Array<any>, dimItemMap: any) => {
  const returnData = blocksData.map((block: any) => {
    const blockDimensions = mergeDimensionsById([
      ...(block?.dimensions || []),
      ...(block?.connected_dimensions || []),
    ]);
    const dims = blockDimensions.map((dimension: any) => {
      const dimensionId = getDimensionId(dimension);
      const items = dimItemMap[dimensionId] || [];
      return {
        dimension_id: dimensionId,
        dimension_name: getDimensionName(dimension),
        dimension_items: items,
      };
    });
    return {
      block_id: block.id,
      block_name: block.name,
      indicators: block.indicators.map((indicator: any) => {
        const buildIndicatorDimension = (dimObject: any, connected: boolean) => {
          const newDimension = dims.find((dimId: any) => {
            return dimObject.indicator_dimension_id === dimId.dimension_id;
          });
          return {
            indicator_dimension_id: dimObject.indicator_dimension_id,
            indicator_dimension_name: dimObject.indicator_dimension_name,
            indicator_dimension_items: mapDimensionItemsToIndicatorItems(
              newDimension?.dimension_items || [],
            ),
            connected,
          };
        };

        const indicatorDimensionMap = new Map<number, any>();
        (indicator?.indicator_dimension || []).forEach((dimObject: any) => {
          indicatorDimensionMap.set(
            Number(dimObject.indicator_dimension_id),
            buildIndicatorDimension(dimObject, false),
          );
        });
        (indicator?.connected_indicator_dimensions || []).forEach((dimObject: any) => {
          indicatorDimensionMap.set(
            Number(dimObject.indicator_dimension_id),
            buildIndicatorDimension(dimObject, true),
          );
        });
        const indDims = Array.from(indicatorDimensionMap.values());

        return {
          indicator_id: indicator.id,
          id: indicator.id,
          indicator_name: indicator.name,
          data_type: indicator.data_type,
          data_format: indicator.data_format,
          indicator_dimensions: indDims,
          connected_indicator_dimensions: indicator.connected_indicator_dimensions,
          indicator_dimension_items: [],
          data_format_properties: indicator.data_format_properties,
        };
      }),
      dimensions: dims,
    };
  });

  return returnData;
};

/**
 * Builds a flat map of indicator_id → indicator_name from dashboard blocks
 * (from /model/{model_id}/blocks). Used to enrich /page/data_values response
 * when the API returns indicator_id instead of indicator name.
 */
const buildIndicatorIdToNameMap = (dashboardBlocks: Array<any>): Record<number, string> => {
  if (!Array.isArray(dashboardBlocks)) return {};
  const returnValue = dashboardBlocks.reduce<Record<number, string>>((accumulator, block) => {
    const indicators = block?.indicators;
    if (!Array.isArray(indicators)) return accumulator;
    indicators.forEach((ind: any) => {
      const id = ind?.indicator_id ?? ind?.id;
      if (id != null && ind?.indicator_name != null) {
        accumulator[Number(id)] = ind.indicator_name;
      }
    });
    return accumulator;
  }, {});
  return returnValue;
};

/**
 * Enriches data_values API response so each data item has indicator.indicator_name
 * resolved from indicator_id using the blocks map (when API returns indicator_id).
 */
const enrichDataValuesWithIndicatorNames = (
  payload: any,
  indicatorIdToName: Record<number, string>,
): any => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const result = { ...payload };
  Object.keys(result).forEach((instrumentId) => {
    const instrumentOutput = result[instrumentId];
    if (!instrumentOutput || !Array.isArray(instrumentOutput.data)) return;
    const enrichedData = instrumentOutput.data.map((item: any) => {
      const indicatorId = item?.indicator_id ?? item?.indicator?.indicator_id;
      const id = indicatorId != null ? Number(indicatorId) : null;
      const nameFromMap = id != null ? indicatorIdToName[id] : undefined;
      const existingName = item?.indicator?.indicator_name ?? item?.indicator?.name;
      const indicatorName = nameFromMap ?? existingName;
      const indicator = Object.assign(item?.indicator ? { ...item.indicator } : {}, {
        indicator_id: item?.indicator_id ?? id ?? item?.indicator?.indicator_id,
      });
      if (indicatorName != null) indicator.indicator_name = indicatorName;
      return { ...item, indicator, indicator_id: indicator.indicator_id };
    });
    result[instrumentId] = { ...instrumentOutput, data: enrichedData };
  });
  return result;
};

/**
 * Enriches a single instrument output (object with .data array) so each item has
 * indicator.indicator_name from the blocks map when API returns indicator_id.
 */
export const enrichSingleInstrumentOutput = (
  payload: any,
  indicatorIdToName: Record<number, string>,
): any => {
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.data)) {
    return payload;
  }
  const enrichedData = payload.data.map((item: any) => {
    const indicatorId = item?.indicator_id ?? item?.indicator?.indicator_id;
    const id = indicatorId != null ? Number(indicatorId) : null;
    const nameFromMap = id != null ? indicatorIdToName[id] : undefined;
    const existingName = item?.indicator?.indicator_name || item?.indicator?.name;
    const indicatorName = nameFromMap ?? existingName;
    const indicator = Object.assign(item?.indicator ? { ...item.indicator } : {}, {
      indicator_id: item?.indicator_id ?? id ?? item?.indicator?.indicator_id,
    });
    if (indicatorName != null) indicator.indicator_name = indicatorName;
    return {
      ...item,
      indicator: { ...indicator, indicator_name: indicatorName },
      indicator_id: indicator.indicator_id,
    };
  });
  return { ...payload, data: enrichedData };
};

export const FetchModelBlocks = createAsyncThunk(
  'dashboardModal/get',
  async ({ modelId }: any, thunkAPI) => {
    try {
      const blocksData = await apiService.getDashboardBlock(modelId);
      const modelDimensions = await apiService.getBuilderDimension(modelId);
      const dimensions = mergeDimensionsById([
        ...(modelDimensions?.data?.dimensions || []),
        ...((blocksData?.data?.blocks || []).flatMap((block: any) => [
          ...(block?.dimensions || []),
          ...(block?.connected_dimensions || []),
        ]) || []),
      ]);
      const dimensionItemsPromises =
        dimensions?.map((dimension: any) =>
          apiService.get_dimension_items(getDimensionId(dimension)),
        ) || [];

      const resolvedDimensionItems = await Promise.all(dimensionItemsPromises);

      const dimItemMap: any = {};
      dimensions.map((dim: any, index: number) => {
        dimItemMap[getDimensionId(dim)] = normalizeDimensionItems(
          resolvedDimensionItems[index]?.data,
        );
        return dimItemMap;
      });
      const returnData = updateDashboardblockData(blocksData.data.blocks, dimItemMap);
      return returnData;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const fetchDashboardID = createAsyncThunk(
  'fetchDashboardID/create',
  async ({ model_id }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.fetch_dashboardid(model_id);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const createDashboardID = createAsyncThunk(
  'createDashboardID/create',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.create_dashboard({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const listModelDashboards = createAsyncThunk(
  'modelDashboards/list',
  async ({ modelId }: { modelId: number | string }, thunkAPI) => {
    try {
      const response = await bloxBiService.listModelDashboards(modelId);
      const rows = Array.isArray(response.data) ? response.data : [];
      return rows.map((row) => mapDashboardListRow(row));
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

export const loadDashboardById = createAsyncThunk(
  'modelDashboards/loadById',
  async ({ dashboardId, modelId }: { dashboardId: string; modelId: number | string }, thunkAPI) => {
    try {
      const response = await bloxBiService.getDashboardById(dashboardId);
      saveSelectedDashboardId(modelId, dashboardId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

/**
 * List dashboards for a model, ensure at least one exists, and load the selected pack.
 * Replaces legacy GET /dashboard/{modelId} for DashboardRevamp bootstrap.
 */
export const initializeModelDashboards = createAsyncThunk(
  'modelDashboards/initialize',
  async ({ model }: { model: any }, thunkAPI) => {
    const modelId = Number(model.id);
    const fetchList = async (): Promise<ModelDashboardListItem[]> => {
      const response = await bloxBiService.listModelDashboards(modelId);
      return (Array.isArray(response.data) ? response.data : []).map((row) =>
        mapDashboardListRow(row),
      );
    };

    let list: ModelDashboardListItem[] = [];
    try {
      list = await fetchList();
    } catch {
      try {
        list = await fetchList();
      } catch {
        list = [];
      }
    }

    if (list.length === 0) {
      try {
        const legacy = await bloxBiService.fetch_dashboardid(modelId);
        if (legacy.data?.id) {
          const dashboard = legacy.data;
          list = [mapDashboardListRow(dashboard)];
          saveSelectedDashboardId(modelId, dashboard.id);
          return {
            list,
            dashboard,
            selectedDashboardId: String(dashboard.id),
          };
        }
      } catch {
        // fall through to create shell
      }

      const created = await bloxBiService.createModelDashboard(
        modelId,
        buildEmptyDashboardBody(model),
      );
      const dashboard = created.data;
      list = [mapDashboardListRow(dashboard)];
      saveSelectedDashboardId(modelId, dashboard.id);
      return {
        list,
        dashboard,
        selectedDashboardId: String(dashboard.id),
      };
    }

    const target = pickDashboardToLoad(list, modelId);
    if (!target) {
      return thunkAPI.rejectWithValue('No dashboard available for model');
    }

    const loaded = await bloxBiService.getDashboardById(target.id);
    saveSelectedDashboardId(modelId, target.id);
    return {
      list,
      dashboard: loaded.data,
      selectedDashboardId: target.id,
    };
  },
);

export const createModelDashboardPack = createAsyncThunk(
  'modelDashboards/create',
  async ({ model, name }: { model: any; name?: string }, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const existing: ModelDashboardListItem[] = state.dashboardSliceV2?.modelDashboards ?? [];
    const resolvedName =
      name?.trim() ||
      suggestNewDashboardName(existing.map((row) => row.name ?? '').filter(Boolean));
    const body = { ...buildEmptyDashboardBody(model), name: resolvedName };
    try {
      const response = await bloxBiService.createModelDashboard(Number(model.id), body);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

export const duplicateModelDashboardPack = createAsyncThunk(
  'modelDashboards/duplicate',
  async ({ dashboardId, name }: { dashboardId: string; name?: string }, thunkAPI) => {
    try {
      const response = await bloxBiService.duplicateDashboard(
        dashboardId,
        name ? { name } : undefined,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

export const deleteModelDashboardPack = createAsyncThunk(
  'modelDashboards/delete',
  async ({ dashboardId, modelId }: { dashboardId: string; modelId: number | string }, thunkAPI) => {
    try {
      await bloxBiService.deleteDashboardById(dashboardId);
      return { dashboardId, modelId };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

export const renameModelDashboardPack = createAsyncThunk(
  'modelDashboards/rename',
  async (
    { dashboardId, name, dashboard }: { dashboardId: string; name: string; dashboard: any },
    thunkAPI,
  ) => {
    try {
      const response = await bloxBiService.updateDashboardById(dashboardId, {
        ...dashboard,
        id: dashboardId,
        name: name.trim(),
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data ?? error.message);
    }
  },
);

export const setDashboardTimeFilter = createAsyncThunk(
  'dashboardTimeFilter/update',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.update_dashboard({ request_body });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchDashboardMetadata = createAsyncThunk(
  'dashboardMetadata/get',
  async ({ dashboardId }: any, thunkAPI) => {
    try {
      const metadata = await bloxBiService.dashboard_metadata(dashboardId);
      return metadata.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const createInstrument = createAsyncThunk(
  'createInstrument/post',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.createDashboardChart({ request_body });
      const state: any = thunkAPI.getState();
      const dashboardBlocks = state.dashboardSliceV2?.dashboardBlocks ?? [];
      const indicatorIdToName = buildIndicatorIdToNameMap(dashboardBlocks);
      // API may return {"data": {"data": [{...}, {...}]}} — series array at response.data.data
      const body = response.data;
      const payloadToEnrich =
        body?.data && typeof body.data === 'object' && Array.isArray(body.data?.data)
          ? body.data
          : body;
      const enriched = enrichSingleInstrumentOutput(payloadToEnrich, indicatorIdToName);
      if (payloadToEnrich !== body) {
        return { ...body, data: enriched };
      }
      return enriched;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateInstrument = createAsyncThunk(
  'updateInstrument/put',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.updateDashboardChart({ request_body });
      const state: any = thunkAPI.getState();
      const dashboardBlocks = state.dashboardSliceV2?.dashboardBlocks ?? [];
      const indicatorIdToName = buildIndicatorIdToNameMap(dashboardBlocks);
      const body = response.data;
      const payloadToEnrich =
        body?.data && typeof body.data === 'object' && Array.isArray(body.data?.data)
          ? body.data
          : body;
      const enriched = enrichSingleInstrumentOutput(payloadToEnrich, indicatorIdToName);
      if (payloadToEnrich !== body) {
        return { ...body, data: enriched };
      }
      return enriched;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteInstrument = createAsyncThunk(
  'delete/deleteInstrument',
  async ({ chartId }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.deleteChart(chartId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updatelayout = createAsyncThunk(
  'updateLayout/put',
  async (request_body: any, thunkAPI) => {
    try {
      const response = await bloxBiService.updateChartLayout({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchDashboardGraph = createAsyncThunk(
  'fetchChartInstrument/get',
  async ({ chartId, scenarioId, requestBody }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.getDashboardGraph(chartId, scenarioId, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchAllDataValues = createAsyncThunk(
  'fetchChartInstrument/get',
  async ({ pageId, scenarioId }: any, thunkAPI) => {
    try {
      const state: any = thunkAPI.getState();
      const timeFilter = state.dashboardSliceV2?.timeFilter;
      const compareDashboardScenario = state.dashboardSliceV2?.compareDashboardScenario;
      const anchorPeriod: string =
        state.dashboardSliceV2?.reporting_anchor_period || getCurrentMonthYyyyMm();
      const window: ReportingWindow = state.dashboardSliceV2?.reporting_window || 'SINGLE';
      const dashboardBlocks = state.dashboardSliceV2?.dashboardBlocks ?? [];

      // Must match TimeRangeFilter / useInstrumentDates: YTD and FY windows depend on FY start.
      const fyStartMonth =
        (state.modelsSlice?.blocks?.time_properties?.fy_start_month as string) || 'Jan';

      // Display timescale (start_date/end_date) must never be blank for DISPLAY_TIMESCALE,
      // because BE expects reporting_start_period/reporting_end_period.
      const modelTimeProperties = state.modelsSlice?.blocks?.time_properties;
      const fallbackStart =
        modelTimeProperties?.starting_period != null
          ? formatMonth(new Date(modelTimeProperties.starting_period))
          : '';
      const fallbackEnd = (() => {
        if (!modelTimeProperties?.starting_period) return '';
        const granularity = (modelTimeProperties?.time_granularity || 'M').toString().toUpperCase();
        const periods = Number(modelTimeProperties?.plan_duration_periods);
        const duration = Number.isNaN(periods) ? (granularity === 'M' ? 36 : 3) : periods;
        const monthsToAdd = granularity === 'M' ? duration : duration * 12;
        const end = new Date(modelTimeProperties.starting_period);
        end.setMonth(end.getMonth() + monthsToAdd);
        return formatMonth(end);
      })();

      const startDate = timeFilter?.start || fallbackStart;
      const endDate = timeFilter?.end || fallbackEnd;
      const compareScenarioId = compareDashboardScenario?.id || null;

      // All windows resolve from reporting_anchor_period (never display timescale end).
      const resolvedReporting =
        window === 'DISPLAY_TIMESCALE'
          ? { start: startDate, end: endDate }
          : resolveReportingPeriod(anchorPeriod, window, fyStartMonth);

      const response = await bloxBiService.getDashboardDataValuesWithTime(
        pageId,
        scenarioId,
        startDate,
        endDate,
        compareScenarioId,
        anchorPeriod,
        window,
        resolvedReporting.start,
        resolvedReporting.end,
      );
      const rawData = response.data;

      // When /page/data_values returns indicator_id instead of indicator name,
      // map indicator_id → indicator_name using /model/{model_id}/blocks data.
      const indicatorIdToName = buildIndicatorIdToNameMap(dashboardBlocks);
      const enrichedData = enrichDataValuesWithIndicatorNames(rawData, indicatorIdToName);

      return enrichInstrumentOutputsDimensionNames(dashboardBlocks, enrichedData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const createPage = createAsyncThunk(
  'createPage/post',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.create_page({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updatePage = createAsyncThunk(
  'updatePage/put',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.update_page({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updatePageOrder = createAsyncThunk(
  'updatePageOrder/put',
  async ({ dashboard }: { dashboard: any }, thunkAPI) => {
    try {
      // Update the entire dashboard with reordered pages
      const response = await bloxBiService.update_dashboard({
        request_body: dashboard,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deletePage = createAsyncThunk(
  'deletePage/delete',
  async ({ pageId }: { pageId: string }, thunkAPI) => {
    try {
      const response = await bloxBiService.delete_page(pageId);
      return { pageId, data: response.data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const dashboardSliceV2 = createSlice({
  initialState,
  name: 'dashboardSliceV2',
  reducers: {
    resetDashboardSliceV2: (state) => {
      state.dashboard = null;
      state.isPageBarOpen = false;
      state.selectedPage = null;
      state.isPageModalOpen = false;
      state.editPageData = {};
      state.createPageStatus = STATUS.IDLE;
      state.updatePageStatus = STATUS.IDLE;
      state.fetchDashboardStatus = STATUS.IDLE;
      state.selectedDashboardScenario = null;
      state.compareDashboardScenario = null;
      state.dashboardBlocks = [];
      state.dashboardBlockStatus = STATUS.IDLE;
      state.dashboardInstruments = [];
      state.editInstrumentStatus = STATUS.IDLE;
      state.editInstrumentData = {};
      state.editInstrumentOutput = {};
      state.allInstruments = [];
      state.timeFilter = null;
      state.allInstrumentOutputs = [];
      state.instrumentExpandKey = {};
      state.allInstrumentOutputsStatus = STATUS.IDLE;
      state.calculationLoadingStatus = STATUS.IDLE;
      state.selectedRow = null;
      state.dashboardReportingPeriod = {
        preset: 'Current Month',
        start: '',
        end: '',
      };
      state.reporting_anchor_period = getCurrentMonthYyyyMm();
      state.reporting_window = 'SINGLE';
      // Keep modelDashboards / selectedDashboardId — header dropdown still needs them
      // when the dashboard tab unmounts and this reset runs.
      state.dashboardPackMutationStatus = STATUS.IDLE;
    },
    setDashboard: (state, action) => {
      state.dashboard = action.payload;
    },
    toggleIsPageBarOpen: (state, action) => {
      state.isPageBarOpen = !state.isPageBarOpen;
      try {
        const modelId = action?.payload;
        if (modelId) {
          const planFilters = localStorage.getItem('global-modal-filters');
          const data = planFilters ? JSON.parse(planFilters) : {};
          data[modelId] = {
            ...data[modelId],
            isPageBarOpen: state.isPageBarOpen,
          };
          localStorage.setItem('global-modal-filters', JSON.stringify(data));
        }
      } catch {
        // Ignore errors when persisting isPageBarOpen to localStorage
      }
    },
    setIsPageBarOpen: (state, action) => {
      state.isPageBarOpen = action.payload;
    },
    setEditInstrumentOutput: (state, action) => {
      state.editInstrumentOutput = action.payload;
    },
    setAllInstrumentOutputs: (state, action) => {
      state.allInstrumentOutputs = action.payload;
    },
    setTimeFilter: (state, action) => {
      const object: any = { start: action.payload.start, end: action.payload.end };
      state.timeFilter = object;
    },
    setDashboardReportingPeriod: (state, action) => {
      state.dashboardReportingPeriod = {
        preset: action.payload.preset || 'Custom',
        start: action.payload.start || '',
        end: action.payload.end || '',
      };
    },
    /**
     * Sets the reporting context (anchor + window) from the new model.
     * Also keeps dashboardReportingPeriod in sync with a matching legacy preset
     * so existing components that read the legacy shape still work.
     */
    setReportingContext: (state, action: { payload: ReportingContext & { preset?: string } }) => {
      // Use direct property access instead of destructuring to avoid snake_case variable names
      state.reporting_anchor_period = action.payload.reporting_anchor_period;
      state.reporting_window = action.payload.reporting_window;
      const { preset } = action.payload;
      // Mirror into legacy shape so TimeRangeFilter / PageInfo don't break immediately
      state.dashboardReportingPeriod = {
        preset: preset || 'Custom',
        start: '',
        end: '',
      };
    },
    selectDashboardScenario: (state, action) => {
      state.selectedDashboardScenario = action.payload;
    },
    selectCompareDashboardScenario: (state, action) => {
      state.compareDashboardScenario = action.payload;
    },
    setDashboardInstrumentStatus: (state, action) => {
      state.dashboardInstrumentStatus = action.payload;
    },
    setEditInstrumentStatus: (state, action) => {
      state.editInstrumentStatus = action.payload;
    },
    setEditInstrumentData: (state, action) => {
      state.editInstrumentData = action.payload;
    },
    setAllInstruments: (state, action) => {
      state.allInstruments = action.payload;
    },
    setInstrumentExpandKey: (state, action) => {
      state.instrumentExpandKey = action.payload;
    },
    setCalculationLocadingStatus: (state, action) => {
      state.calculationLoadingStatus = action.payload;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    prepareCreatePayload: (state, action) => {
      state.editInstrumentData = {
        instrument_type: action.payload,
        instrument_indicators: [],
        dashboard_id: state.dashboard?.id,
        start_date: state.timeFilter?.start || 'Jan-24',
        end_date: state.timeFilter?.end || 'Dec-26',
        instrument_filters: [
          {
            instrument_id: null,
            name: 'Time',
            operator: 'between',
            value: state.timeFilter?.start || 'Jan-24',
            second_value: state.timeFilter?.end || 'Dec-26',
            filter_type: 'primary',
          },
          // TODO:  Global Time  Filter
        ],
        time_granularity: 'month', // "Input should be 'all', 'month', 'quarter' or 'year'"
        id: null,
        scenario_id: state.selectedDashboardScenario.id.toString(),
        compare_scenario: state.compareDashboardScenario?.id || null,
        name:
          action.payload === 'line' || action.payload === 'bar' || action.payload === 'stack'
            ? 'Chart'
            : `${action.payload}`.charAt(0).toUpperCase() + `${action.payload}`.slice(1),
        page_id: state.selectedPage?.id,
        // TODO: unused should be removed
        formatting: {
          units: 'auto',
          sparkline: false,
          decimalPlaces: 0,
          stackBar: false,
          negativeDisplay: 'Minus Sign',
          columns_settings: {
            actual_column_name: '',
            previous_year: true,
            varience_to_py: true,
            varience_percentage_py: true,
            plan_comparison_column_name: '',
            plan: true,
            varience_to_pl: true,
            varience_percentage_pl: true,
          },
          columns_position: {
            previous_year: 1,
            varience_to_py: 2,
            varience_percentage_py: 3,
            plan: 4,
            varience_to_pl: 5,
            varience_percentage_pl: 6,
            plan_comparison_column_name: 7,
            actual_column_name: 8,
          },
        },
        reporting_period: {
          preset: 'Current Month',
          start: '',
          end: '',
        },
        instrument_sorts: [],
        instrument_dimensions: [],
        conditional_formatting: [],
        position: 0,
        width: 4,
        height: 2,
      };
    },
    handleSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    },
    handleEditCancel: (state) => {
      state.editInstrumentData = {};
      state.editInstrumentOutput = {};
      state.selectedRow = null;
    },
    togglePageModal: (state) => {
      state.isPageModalOpen = !state.isPageModalOpen;
      if (!state.isPageModalOpen) {
        state.editPageData = {};
      }
    },
    setEditPageData: (state, action) => {
      state.editPageData = action.payload;
    },
    openPageModal: (state, action) => {
      state.isPageModalOpen = true;
      state.editPageData = action.payload || {};
    },
    closePageModal: (state) => {
      state.isPageModalOpen = false;
      state.editPageData = {};
    },
    reorderPages: (state, action) => {
      if (state.dashboard?.pages) {
        state.dashboard.pages = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(FetchModelBlocks.pending, (state) => {
        state.dashboardBlockStatus = STATUS.LOADING;
      })
      .addCase(FetchModelBlocks.rejected, (state) => {
        state.dashboardBlockStatus = STATUS.FAILED;
      })
      .addCase(FetchModelBlocks.fulfilled, (state, action) => {
        state.dashboardBlocks = action.payload;
        state.dashboardBlockStatus = STATUS.SUCCESS;
        if (state.allInstruments?.length) {
          state.allInstruments = enrichAllInstrumentsInstrumentIndicatorsDimensionNames(
            state.dashboardBlocks,
            state.allInstruments,
          );
        }
        if (
          state.allInstrumentOutputs &&
          typeof state.allInstrumentOutputs === 'object' &&
          !Array.isArray(state.allInstrumentOutputs) &&
          Object.keys(state.allInstrumentOutputs).length > 0
        ) {
          const indicatorIdToName = buildIndicatorIdToNameMap(state.dashboardBlocks);
          const outputsWithIndicatorNames = enrichDataValuesWithIndicatorNames(
            state.allInstrumentOutputs,
            indicatorIdToName,
          );
          state.allInstrumentOutputs = enrichInstrumentOutputsDimensionNames(
            state.dashboardBlocks,
            outputsWithIndicatorNames,
          );
        }
      })
      .addCase(fetchDashboardID.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.fetchDashboardStatus = STATUS.SUCCESS;
        if (action.payload?.id) {
          state.selectedDashboardId = String(action.payload.id);
        }
      })
      .addCase(createDashboardID.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.fetchDashboardStatus = STATUS.SUCCESS;
        if (action.payload?.id) {
          state.selectedDashboardId = String(action.payload.id);
          state.modelDashboards = [
            mapDashboardListRow(action.payload),
            ...state.modelDashboards.filter((row) => row.id !== String(action.payload.id)),
          ];
        }
      })
      .addCase(initializeModelDashboards.pending, (state) => {
        state.fetchDashboardStatus = STATUS.LOADING;
        state.listModelDashboardsStatus = STATUS.LOADING;
      })
      .addCase(initializeModelDashboards.fulfilled, (state, action) => {
        state.modelDashboards = action.payload.list;
        state.dashboard = action.payload.dashboard;
        state.selectedDashboardId = action.payload.selectedDashboardId;
        state.fetchDashboardStatus = STATUS.SUCCESS;
        state.listModelDashboardsStatus = STATUS.SUCCESS;
        state.selectedPage = null;
        state.allInstruments = [];
        state.allInstrumentOutputs = [];
      })
      .addCase(initializeModelDashboards.rejected, (state) => {
        state.fetchDashboardStatus = STATUS.FAILED;
        state.listModelDashboardsStatus = STATUS.FAILED;
      })
      .addCase(listModelDashboards.fulfilled, (state, action) => {
        state.modelDashboards = action.payload;
        state.listModelDashboardsStatus = STATUS.SUCCESS;
      })
      .addCase(listModelDashboards.pending, (state) => {
        state.listModelDashboardsStatus = STATUS.LOADING;
      })
      .addCase(loadDashboardById.pending, (state) => {
        state.fetchDashboardStatus = STATUS.LOADING;
      })
      .addCase(loadDashboardById.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.selectedDashboardId = String(action.payload.id);
        state.fetchDashboardStatus = STATUS.SUCCESS;
        state.selectedPage = null;
        state.allInstruments = [];
        state.allInstrumentOutputs = [];
        const row = mapDashboardListRow(action.payload);
        if (!state.modelDashboards.some((pack) => pack.id === row.id)) {
          state.modelDashboards = [...state.modelDashboards, row];
        }
      })
      .addCase(loadDashboardById.rejected, (state) => {
        state.fetchDashboardStatus = STATUS.FAILED;
      })
      .addCase(createModelDashboardPack.pending, (state) => {
        state.dashboardPackMutationStatus = STATUS.LOADING;
      })
      .addCase(createModelDashboardPack.fulfilled, (state, action) => {
        state.dashboardPackMutationStatus = STATUS.SUCCESS;
        const row = mapDashboardListRow(action.payload);
        state.modelDashboards = [row, ...state.modelDashboards.filter((d) => d.id !== row.id)];
        state.dashboard = action.payload;
        state.selectedDashboardId = row.id;
        state.selectedPage = null;
        state.allInstruments = [];
        state.allInstrumentOutputs = [];
      })
      .addCase(createModelDashboardPack.rejected, (state) => {
        state.dashboardPackMutationStatus = STATUS.FAILED;
      })
      .addCase(duplicateModelDashboardPack.pending, (state) => {
        state.dashboardPackMutationStatus = STATUS.LOADING;
      })
      .addCase(duplicateModelDashboardPack.fulfilled, (state, action) => {
        state.dashboardPackMutationStatus = STATUS.SUCCESS;
        const row = mapDashboardListRow(action.payload);
        state.modelDashboards = [row, ...state.modelDashboards.filter((d) => d.id !== row.id)];
        state.dashboard = action.payload;
        state.selectedDashboardId = row.id;
        state.selectedPage = null;
        state.allInstruments = [];
        state.allInstrumentOutputs = [];
      })
      .addCase(duplicateModelDashboardPack.rejected, (state) => {
        state.dashboardPackMutationStatus = STATUS.FAILED;
      })
      .addCase(deleteModelDashboardPack.pending, (state) => {
        state.dashboardPackMutationStatus = STATUS.LOADING;
      })
      .addCase(deleteModelDashboardPack.fulfilled, (state, action) => {
        state.dashboardPackMutationStatus = STATUS.SUCCESS;
        state.modelDashboards = state.modelDashboards.filter(
          (row) => row.id !== action.payload.dashboardId,
        );
        if (state.selectedDashboardId === action.payload.dashboardId) {
          state.dashboard = null;
          state.selectedDashboardId = null;
          state.selectedPage = null;
          state.allInstruments = [];
          state.allInstrumentOutputs = [];
        }
      })
      .addCase(deleteModelDashboardPack.rejected, (state) => {
        state.dashboardPackMutationStatus = STATUS.FAILED;
      })
      .addCase(renameModelDashboardPack.fulfilled, (state, action) => {
        state.dashboardPackMutationStatus = STATUS.SUCCESS;
        const id = String(action.payload.id);
        state.modelDashboards = state.modelDashboards.map((row) =>
          row.id === id
            ? { ...row, name: action.payload.name, updated_at: action.payload.updated_at }
            : row,
        );
        if (state.dashboard?.id === action.payload.id) {
          state.dashboard = { ...state.dashboard, name: action.payload.name };
        }
      })
      .addCase(fetchDashboardMetadata.fulfilled, (state, action) => {
        const instruments = action.payload?.instruments;
        state.allInstruments =
          Array.isArray(state.dashboardBlocks) && state.dashboardBlocks.length > 0
            ? enrichAllInstrumentsInstrumentIndicatorsDimensionNames(
                state.dashboardBlocks,
                instruments,
              )
            : instruments;
      })
      .addCase(fetchAllDataValues.pending, (state) => {
        state.allInstrumentOutputsStatus = STATUS.LOADING;
      })
      .addCase(fetchAllDataValues.rejected, (state) => {
        state.allInstrumentOutputsStatus = STATUS.FAILED;
      })
      .addCase(fetchAllDataValues.fulfilled, (state, action) => {
        state.allInstrumentOutputs = action.payload;
        state.allInstrumentOutputsStatus = STATUS.SUCCESS;
      })
      .addCase(createInstrument.pending, (state) => {
        state.calculationLoadingStatus = STATUS.LOADING;
      })
      .addCase(createInstrument.fulfilled, (state) => {
        state.calculationLoadingStatus = STATUS.SUCCESS;
      })
      .addCase(createInstrument.rejected, (state) => {
        state.calculationLoadingStatus = STATUS.FAILED;
      })
      .addCase(updateInstrument.pending, (state) => {
        state.calculationLoadingStatus = STATUS.LOADING;
      })
      .addCase(updateInstrument.fulfilled, (state) => {
        state.calculationLoadingStatus = STATUS.SUCCESS;
      })
      .addCase(updateInstrument.rejected, (state) => {
        state.calculationLoadingStatus = STATUS.FAILED;
      })
      .addCase(createPage.pending, (state) => {
        state.createPageStatus = STATUS.LOADING;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.createPageStatus = STATUS.SUCCESS;
        // Add the new page to the dashboard pages array
        if (state.dashboard?.pages) {
          state.dashboard.pages.push(action.payload);
        } else if (state.dashboard) {
          state.dashboard.pages = [action.payload];
        }
        // Automatically select the newly created page
        state.selectedPage = action.payload;
        state.isPageModalOpen = false;
        state.editPageData = {};
      })
      .addCase(createPage.rejected, (state) => {
        state.createPageStatus = STATUS.FAILED;
      })
      .addCase(updatePage.pending, (state) => {
        state.updatePageStatus = STATUS.LOADING;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        state.updatePageStatus = STATUS.SUCCESS;
        // Update the page in the dashboard pages array
        if (state.dashboard?.pages) {
          const pageIndex = state.dashboard.pages.findIndex(
            (page: any) => page.id === action.payload.id,
          );
          if (pageIndex !== -1) {
            state.dashboard.pages[pageIndex] = action.payload;
          }
        }
        if (state.selectedPage?.id === action.payload.id) {
          state.selectedPage = action.payload;
        }
        state.isPageModalOpen = false;
        state.editPageData = {};
      })
      .addCase(updatePage.rejected, (state) => {
        state.updatePageStatus = STATUS.FAILED;
      })
      .addCase(deletePage.pending, (state) => {
        state.deletePageStatus = STATUS.LOADING;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.deletePageStatus = STATUS.SUCCESS;
        // Remove the page from the dashboard pages array
        if (state.dashboard?.pages) {
          state.dashboard.pages = state.dashboard.pages.filter(
            (page: any) => page.id !== action.payload.pageId,
          );
        }
        // If the deleted page was selected, select the first page or null
        if (state.selectedPage?.id === action.payload.pageId) {
          state.selectedPage = state.dashboard?.pages?.[0] || null;
        }
      })
      .addCase(deletePage.rejected, (state) => {
        state.deletePageStatus = STATUS.FAILED;
      });
  },
});
export const {
  setTimeFilter,
  setDashboardReportingPeriod,
  setReportingContext,
  resetDashboardSliceV2,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  setDashboardInstrumentStatus,
  setEditInstrumentStatus,
  setEditInstrumentData,
  setAllInstruments,
  prepareCreatePayload,
  setEditInstrumentOutput,
  setAllInstrumentOutputs,
  setInstrumentExpandKey,
  setDashboard,
  handleSelectedRow,
  handleEditCancel,
  setCalculationLocadingStatus,
  setSelectedPage,
  toggleIsPageBarOpen,
  setIsPageBarOpen,
  togglePageModal,
  setEditPageData,
  openPageModal,
  closePageModal,
  reorderPages,
} = dashboardSliceV2.actions;
export const getTimeFilter = (state: any) => state.dashboardSliceV2.timeFilter;
export const getDashboardReportingPeriod = (state: any) =>
  state.dashboardSliceV2.dashboardReportingPeriod;
export const getDashboard = (state: any) => state.dashboardSliceV2.dashboard;
export const getDashboardBlocks = (state: any) => state.dashboardSliceV2.dashboardBlocks;
export const getDashboardBlockStatus = (state: any) => state.dashboardSliceV2.dashboardBlockStatus;
export const getAllInstruments = (state: any) => state.dashboardSliceV2.allInstruments;
export const getSelectedDashboardScenario = (state: any) =>
  state.dashboardSliceV2.selectedDashboardScenario;
export const getCompareDashboardScenario = (state: any) =>
  state.dashboardSliceV2.compareDashboardScenario;
export const getFetchDashboardStatus = (state: any) => state.dashboardSliceV2.fetchDashboardStatus;
export const getDashboardInstruments = (state: any) => state.dashboardSliceV2.dashboardInstruments;
export const getDashboardInstrumentStatus = (state: any) =>
  state.dashboardSliceV2.dashboardInstrumentStatus;
export const getEditInstrumentStatus = (state: any) => state.dashboardSliceV2.editInstrumentStatus;
export const getEditInstrumentData = (state: any) => state.dashboardSliceV2.editInstrumentData;
export const getEditInstrumentOutput = (state: any) => state.dashboardSliceV2.editInstrumentOutput;
export const getAllInstrumentOutputs = (state: any) => state.dashboardSliceV2.allInstrumentOutputs;
export const getInstrumentExpandKey = (state: any) => state.dashboardSliceV2.instrumentExpandKey;
export const getCalculationLoadingStatus = (state: any) =>
  state.dashboardSliceV2.calculationLoadingStatus;
export const getAllInstrumentOutputsStatus = (state: any) =>
  state.dashboardSliceV2.allInstrumentOutputsStatus;
export const getSelectedRow = (state: any) => state.dashboardSliceV2.selectedRow;
export const getSelectedPage = (state: any) => state.dashboardSliceV2.selectedPage;
export const getIsPageBarOpen = (state: any) => state.dashboardSliceV2.isPageBarOpen;
export const getIsPageModalOpen = (state: any) => state.dashboardSliceV2.isPageModalOpen;
export const getEditPageData = (state: any) => state.dashboardSliceV2.editPageData;
export const getCreatePageStatus = (state: any) => state.dashboardSliceV2.createPageStatus;
export const getUpdatePageStatus = (state: any) => state.dashboardSliceV2.updatePageStatus;
export const getDeletePageStatus = (state: any) => state.dashboardSliceV2.deletePageStatus;
export const getReportingAnchorPeriod = (state: any) =>
  state.dashboardSliceV2.reporting_anchor_period as string;
export const getReportingWindow = (state: any) =>
  state.dashboardSliceV2.reporting_window as ReportingWindow;
export const getModelDashboards = (state: any) =>
  state.dashboardSliceV2.modelDashboards as ModelDashboardListItem[];
export const getSelectedDashboardId = (state: any) =>
  state.dashboardSliceV2.selectedDashboardId as string | null;

export default dashboardSliceV2.reducer;
